'use client';

import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { PortableText } from "@portabletext/react";
import { getResiseImageUrl, urlForImage } from "../../../sanity/lib/image";
import Link from "next/link";
import Image from "next/image";
import { BannerRichTextComponents } from "@/components/BannerRichTextComponents";
import React, {useMemo, useState} from "react";
import {ChevronLeft, ChevronRight} from "lucide-react";

interface SliderPrincipaleProps {
    banners: any[];
}

export default function SliderPrincipale({ banners }: SliderPrincipaleProps) {
    const [carouselApi, setCarouselApi] = useState<any>();
    const desktopHeroWidth = 1600;
    const desktopHeroHeight = 900;
    const mobileHeroWidth = 960;
    const mobileHeroHeight = 1280;

    const normalizeImage = (image?: any, fallback?: any) => {
        if (image) {
            return image;
        }

        return fallback ?? null;
    };

    const getImageUrl = (image: any) => {
        if (!image) {
            return null;
        }

        try {
            if (typeof image === 'string') {
                return image;
            }

            return urlForImage(image)
                .width(desktopHeroWidth)
                .height(desktopHeroHeight)
                .url();
        } catch (error) {
            return typeof image === 'string' ? image : null;
        }
    };

    const getMobileImageUrl = (image: any) => {
        if (!image) {
            return null;
        }

        try {
            if (typeof image === 'string') {
                return image;
            }

            return urlForImage(image)
                .width(mobileHeroWidth)
                .height(mobileHeroHeight)
                .url();
        } catch (error) {
            return typeof image === 'string' ? image : null;
        }
    };

    const heroBanners = useMemo(() => {
        return banners.map((banner: any) => {
            const desktopImage = normalizeImage(banner.image, banner.mobileImage);
            const mobileImage = normalizeImage(banner.mobileImage, banner.image);

            return {
                ...banner,
                desktopImage,
                desktopImageUrl: getImageUrl(desktopImage),
                mobileImage,
                mobileImageUrl: getMobileImageUrl(mobileImage),
            };
        });
    }, [banners]);

    const getButtonClasses = (banner: any, baseClasses: string) => {
        const gradient = banner?.buttonColor ? '' : 'bg-gradient-to-r from-[#000D7D] to-[#001AFF]';
        return `${baseClasses} ${gradient}`.trim();
    };


    return (
        <div className="relative w-full min-h-[65vh] sm:min-h-[75vh]">
            <Carousel
                className="h-full w-full"
                opts={{
                    align: "center",
                    loop: true,
                }}
                setApi={setCarouselApi}
            >
                <CarouselContent className="h-full">
                    {heroBanners.map((banner, index) => (
                        <CarouselItem key={banner._id}>
                            <div className="relative min-h-[65vh] sm:min-h-[75vh] w-full">
                                {/* Full-screen background image */}
                                <div className="absolute inset-0">
                                    {banner.desktopImageUrl && (
                                        <Image
                                            src={banner.desktopImageUrl}
                                            alt={banner?.seo?.description || 'Gruppo Piccirillo'}
                                            fill
                                            sizes="(max-width: 1024px) 100vw, 100vw"
                                            style={{ objectFit: 'cover' }}
                                            placeholder="blur"
                                            blurDataURL={getResiseImageUrl(banner.desktopImage, 10, 10)}
                                            priority={index === 0}
                                            quality={70}
                                            className="hidden sm:block"
                                        />
                                    )}
                                    {banner.mobileImageUrl && (
                                        <Image
                                            src={banner.mobileImageUrl}
                                            alt={banner?.seo?.description || 'Gruppo Piccirillo'}
                                            fill
                                            sizes="100vw"
                                            style={{ objectFit: 'cover' }}
                                            placeholder="blur"
                                            blurDataURL={getResiseImageUrl(banner.mobileImage, 10, 10)}
                                            priority={index === 0}
                                            quality={65}
                                            className="sm:hidden"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60 sm:via-black/10 sm:to-black/40" aria-hidden="true" />
                                </div>

                                {/* Mobile title positioning */}
                                <div className="absolute bottom-12 sm:hidden left-0 right-0 text-white z-20 pb-6 px-4">
                                    <div className="max-w-2xl mx-auto w-full bg-black/55 backdrop-blur-sm rounded-2xl px-4 py-6 space-y-5">
                                        <div className="text-base leading-snug font-semibold">
                                            <PortableText value={banner.mobileTitle?.length ? banner.mobileTitle : banner.title} components={BannerRichTextComponents} />
                                        </div>
                                        {banner.showButton !== false && banner.linkUrl && (banner.mobileButtonText || banner.buttonText) && (
                                            <Link href={banner.linkUrl} hrefLang="it" title={banner.mobileButtonText || banner.buttonText}>
                                                <button
                                                    className={getButtonClasses(banner, 'w-full px-6 py-3 text-sm font-semibold text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 text-center')}
                                                    style={banner.buttonColor ? { background: banner.buttonColor } : {}}
                                                >
                                                    {banner.mobileButtonText || banner.buttonText}
                                                </button>
                                            </Link>
                                        )}
                                    </div>
                                </div>

                                {/* Desktop content - positioned at bottom */}
                                <div className="absolute bottom-0 max-sm:hidden left-0 right-0 text-white z-20 pb-6">
                                    <div className="text-center max-w-7xl mx-auto px-4">
                                        <div className="text-2xl md:text-3xl max-sm:hidden lg:text-4xl mb-6 leading-tight font-semibold drop-shadow">
                                            <PortableText value={banner.title} components={BannerRichTextComponents} />
                                        </div>
                                        {banner.showButton !== false && banner.linkUrl && banner.buttonText && (
                                            <div className="mt-8">
                                                <Link hrefLang="it" title={banner.buttonText} href={banner.linkUrl}>
                                                    <button
                                                        className={getButtonClasses(banner, 'px-8 md:px-12 py-3 text-white font-bold rounded-md hover:shadow-lg transition-all duration-300 text-lg md:text-xl inline-flex items-center justify-center')}
                                                        style={banner.buttonColor ? { background: banner.buttonColor } : {}}
                                                    >
                                                        {banner.buttonText}
                                                    </button>
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>

                {/* Custom Navigation arrows with Lucide icons */}
                <div className="absolute left-4 sm:bottom-12 bottom-4 flex gap-3">
                    <button
                        onClick={() => carouselApi?.scrollPrev()}
                        className="h-12 w-12 rounded-full hover:bg-white/30 border-2 border-white text-white flex items-center justify-center transition-all duration-200"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <button
                        onClick={() => carouselApi?.scrollNext()}
                        className="h-12 w-12 rounded-full hover:bg-white/30 border-2 border-white text-white flex items-center justify-center transition-all duration-200"
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            </Carousel>
        </div>
    );
}
